import { ref } from 'vue'
import type SchoolYear from '@/core/domain/school/year/SchoolYear.ts'
import type { LearningModule } from '@/core/domain/learning/module/LearningModule.ts'
import type { Quiz } from '@/core/domain/quiz/Quiz.ts'
import type { UserSelfView } from '@/core/domain/users/LocalUser.ts'

// Définir les types d'événements et leurs payloads
export interface StoreEvents {
  // Événements d'authentification
  'auth-initialized': {
    authenticated: boolean
  }
  'auth-failed': {
    error: string
  }
  'auth-before-logout': Record<string, never>
  'auth-logged-out': Record<string, never>

  // Événements d'initialisation de l'application
  'app-initialized': {
    success: boolean
  }
  'app-initialization-failed': {
    error: string
  }

  // Événements liés au profil utilisateur
  'user-profile-loaded': {
    user: UserSelfView
  }

  // Événements métier
  'group-updated': {
    schoolId: number
    schoolPeriodId: number
    schoolClassroomId: number
    groupId: number
  }
  'classroom-updated': {
    schoolId: number
    schoolPeriodId: number
    classroomId: number
  }
  'school:changed': {
    schoolId: number
  }
  'schoolYear:changed': SchoolYear | null
  'schoolYear:active:changed': SchoolYear | null
  'dashboard:refresh': void
  'schools:refresh': void
  'learning:refresh': void

  'learning:module:updated': {
    moduleId: number
    courseId: number
    module: LearningModule
  }

  'learning:quizdiag:updated': {
    quiz: Quiz
  }
  'learning:quizgrain:updated': {
    quiz: Quiz
  }

  'learning:grain:deleted': {
    grainId: number
    moduleId: number
    courseId: number
  }
}

// Type pour un événement enregistré avec discriminant
export type StoredEvent = {
  [K in keyof StoreEvents]: { name: K; timestamp: number; payload: StoreEvents[K] }
}[keyof StoreEvents]

// Référence pour le dernier événement
export const lastEvent = ref<StoredEvent | null>(null)

// Historique des événements
const MAX_HISTORY_SIZE = 50
export const eventHistory = ref<StoredEvent[]>([])

type EventOf<K extends keyof StoreEvents> = Extract<StoredEvent, { name: K }>


// Fonction pour émettre un événement
export function emitStoreEvent<K extends keyof StoreEvents>(
  eventName: K,
  payload?: StoreEvents[K]
): void {
  console.log('🚀 Emitting event:', eventName, 'with payload:', payload)

  const event = {
    name: eventName,
    timestamp: Date.now(),
    payload
  } as StoredEvent

  // Mettre à jour le dernier événement
  lastEvent.value = event

  // Ajouter à l'historique
  eventHistory.value.push(event)
  if (eventHistory.value.length > MAX_HISTORY_SIZE) {
    eventHistory.value.shift() // Supprimer l'événement le plus ancien si l'historique dépasse la taille maximale
  }

  // Notifier tous les abonnés
  const eventListeners = listeners[eventName]
  console.log('📡 Found', eventListeners?.length || 0, 'listeners for event:', eventName)

  if (eventListeners) {
    eventListeners.forEach((listener, index) => {
      try {
        console.log(`📨 Calling listener ${index + 1} for event:`, eventName)
        listener(payload as any)
        console.log(`✅ Listener ${index + 1} executed successfully`)
      } catch (error) {
        console.error(`❌ Error in listener ${index + 1} for ${eventName}:`, error)
      }
    })
  } else {
    console.warn('⚠️ No listeners found for event:', eventName)
  }

  console.log('📝 Event logged:', event)
}

// Fonction pour s'abonner à un événement
type EventListener<K extends keyof StoreEvents> = (payload: StoreEvents[K]) => void
const listeners: { [K in keyof StoreEvents]?: EventListener<K>[] } = {}

// S'abonner à un événement
export function onStoreEvent<K extends keyof StoreEvents>(
  eventName: K,
  listener: EventListener<K>
): void {
  console.log('🎯 Registering listener for event:', eventName)

  if (!listeners[eventName]) {
    listeners[eventName] = []
  }
  listeners[eventName]?.push(listener)

  console.log('📊 Total listeners for', eventName, ':', listeners[eventName]?.length || 0)
  console.log(listeners[eventName])
}

// Se désabonner d'un événement
export function offStoreEvent<K extends keyof StoreEvents>(
  eventName: K,
  listener: EventListener<K>
): void {
  const eventListeners = listeners[eventName]
  if (!eventListeners) return

  const index = eventListeners.indexOf(listener)
  if (index !== -1) {
    eventListeners.splice(index, 1)
  }
}

// Fonction pour filtrer l'historique par type d'événement
export function getEventHistoryByType<K extends keyof StoreEvents>(
  eventName: K,
  limit?: number
): EventOf<K>[] {
  const filteredEvents = eventHistory.value.filter(
    (e): e is EventOf<K> => e.name === eventName
  )
  return typeof limit === 'number' ? filteredEvents.slice(-limit) : filteredEvents
}

// Fonction pour vider l'historique
export function clearEventHistory(): void {
  eventHistory.value = []
}

// Hook pour créer un abonnement lié au cycle de vie du composant ou utilisable dans les stores
export function useEventListener<K extends keyof StoreEvents>(
  eventName: K,
  callback: EventListener<K>,
  options: { immediate?: boolean; once?: boolean } = {}
) {
  const { immediate = false, once = false } = options

  // Wrapper pour gérer l'option "once"
  let wrappedCallback: EventListener<K>

  if (once) {
    wrappedCallback = ((payload: StoreEvents[K]) => {
      callback(payload)
      offStoreEvent(eventName, wrappedCallback)
    }) as EventListener<K>
  } else {
    wrappedCallback = callback
  }

  console.log('🎭 registering listener for:', eventName)
  onStoreEvent(eventName, wrappedCallback)

  // Exécution immédiate si demandé et un événement existe déjà
  if (immediate && lastEvent.value && lastEvent.value.name === eventName) {
    callback(lastEvent.value.payload as any)
  }
}

// Hook pour utiliser les événements
export function useStoreEvents() {
  return {
    lastEvent,
    eventHistory,
    emit: emitStoreEvent,
    on: onStoreEvent,
    off: offStoreEvent,
    useEventListener,
    getEventHistoryByType,
    clearEventHistory
  }
}

// Exportation d'un singleton du bus d'événements pour faciliter l'utilisation
export const storeEventBus = {
  emit: emitStoreEvent,
  on: onStoreEvent,
  off: offStoreEvent,
  useEventListener,
  lastEvent,
  eventHistory,
  getEventHistoryByType,
  clearEventHistory
}
